import React, { useCallback, useEffect, useState } from 'react';
import './mcp.css';
import { Alert, Spin } from 'antd';
import { ApiOutlined, CarOutlined, CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { authHeader } from '../../../utils/api';
import McpHeader from '../../../components/mcp/McpHeader';

/* ── helpers ──────────────────────────────────────────────────────────────── */

function formatRelative(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString();
}

/** Normalise an API response that may be an array or object with a rows key */
function toArray(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.accounts)) return data.accounts;
  if (Array.isArray(data.data)) return data.data;
  return [data];
}

/* ── sub-components ───────────────────────────────────────────────────────── */

function KV({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--paper-300)' }}>
      <span style={{ color: 'var(--ink-200)' }}>{label}</span>
      <span style={{ fontWeight: 600, color: 'var(--ink)', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(value || '')}>{value || '—'}</span>
    </div>
  );
}

function StatusPill({ connected }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: connected ? 'var(--sage)' : 'var(--ink-200)' }}>
      {connected
        ? <><CheckCircleFilled /> Connected</>
        : <><CloseCircleFilled /> Not connected</>}
    </div>
  );
}

function ConnectionCard({ icon, title, category, description, connected, accountCount, details, onReauth, onConnect, onDisconnect, disconnectLoading }) {
  return (
    <div className="mcp-card3d" style={{ padding: 24, display: 'flex', flexDirection: 'column' }}>
      {/* Icon + status row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14, flexShrink: 0,
          background: connected ? 'var(--ink)' : 'var(--paper-300)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
        }}>
          {icon}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <StatusPill connected={connected} />
          {connected && accountCount > 1 && (
            <span style={{ fontSize: 11, color: 'var(--ink-300)' }}>{accountCount} account{accountCount !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {/* Title + eyebrow */}
      <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--ink)', marginBottom: 2, letterSpacing: '-0.02em' }}>{title}</div>
      <div className="mcp-eyebrow" style={{ marginBottom: 10 }}>{category}</div>
      <p style={{ fontSize: 13, color: 'var(--ink-100)', lineHeight: 1.55, margin: '0 0 16px' }}>{description}</p>

      {/* Connected details */}
      {connected && details && details.length > 0 && (
        <div style={{ background: 'var(--paper-200)', borderRadius: 12, padding: '8px 14px', marginBottom: 16 }}>
          {details.map(d => <KV key={d.label} label={d.label} value={d.value} />)}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        {connected ? (
          <>
            <button
              type="button"
              className="mcp-btn-ghost flex-1 justify-center"
              onClick={onReauth || onConnect}
            >
              Reauthorize
            </button>
            <button
              type="button"
              className="mcp-btn-ghost text-ember hover:text-ember-dark"
              disabled={!!disconnectLoading}
              onClick={onDisconnect}
            >
              {disconnectLoading ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="mcp-btn-sun w-full justify-center"
            onClick={onConnect}
          >
            Connect now
          </button>
        )}
      </div>
    </div>
  );
}

/* ── page ─────────────────────────────────────────────────────────────────── */

const McpConnections = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading]       = useState(true);
  const [geotabAccs, setGeotabAccs] = useState([]);  // [{ id, database, username, base_url, updated_at }]
  const [metaAccs, setMetaAccs]     = useState([]);  // [{ id, account_name, account_email, connected_at, access_token }]
  const [googleAccs, setGoogleAccs] = useState([]);  // [{ id, account_name, account_email, connected_at, access_token, login_customer_id }]
  const [error, setError]           = useState(null);
  const [disconnecting, setDisconnecting] = useState(null); // 'geotab' | 'meta' | 'google'

  /* ── fetch all three providers ── */
  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      // Geotab — list stored accounts
      fetch('/api/integrations/geotab/accounts', { headers: authHeader() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
      // Meta — list connected Facebook/Meta accounts
      fetch('/api/integrations/meta', { headers: authHeader() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
      // Google Ads — list connected Google accounts
      fetch('/api/integrations/google-ads', { headers: authHeader() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([g, m, ga]) => {
      // Geotab: rows have id, database, username, base_url, updated_at
      setGeotabAccs(toArray(g).filter(a => a.database || a.username));
      // Meta: rows have access_token — only count as connected if token present
      setMetaAccs(toArray(m).filter(a => a.access_token));
      // Google: rows have access_token
      setGoogleAccs(toArray(ga).filter(a => a.access_token));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /* ── disconnect handlers ── */
  async function disconnectGeotab() {
    if (!geotabAccs[0]?.id) return navigate('/client/addins');
    setDisconnecting('geotab');
    try {
      const res = await fetch(`/api/integrations/geotab/accounts/${geotabAccs[0].id}`, {
        method: 'DELETE', headers: authHeader(),
      });
      if (!res.ok) throw new Error('Failed');
      setGeotabAccs([]);
    } catch {
      setError('Could not disconnect Geotab. Try again.');
    } finally {
      setDisconnecting(null);
    }
  }

  async function disconnectMeta() {
    const id = metaAccs[0]?.id;
    if (!id) return;
    setDisconnecting('meta');
    try {
      const res = await fetch('/api/integrations/meta/disconnect', {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: id }),
      });
      if (!res.ok) throw new Error('Failed');
      setMetaAccs([]);
    } catch {
      setError('Could not disconnect Meta. Try again.');
    } finally {
      setDisconnecting(null);
    }
  }

  async function disconnectGoogle() {
    const id = googleAccs[0]?.id;
    if (!id) return;
    setDisconnecting('google');
    try {
      const res = await fetch('/api/integrations/google-ads/disconnect', {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: id }),
      });
      if (!res.ok) throw new Error('Failed');
      setGoogleAccs([]);
    } catch {
      setError('Could not disconnect Google Ads. Try again.');
    } finally {
      setDisconnecting(null);
    }
  }

  const connectedCount = [geotabAccs, metaAccs, googleAccs].filter(a => a.length > 0).length;
  const geotab = geotabAccs[0];
  const meta   = metaAccs[0];
  const google = googleAccs[0];

  /* ── direct connect handlers ── */
  const backendUrl = (() => {
    const explicit = process.env.REACT_APP_API_URL;
    if (explicit) return explicit.replace(/\/api$/, '');
    const host = window.location.hostname;
    if (['app.pathmarkr.com', 'pathmarkr.com', 'www.pathmarkr.com'].includes(host)) return 'https://api.pathmarkr.com';
    if (host.includes('s3-website') || host.includes('amazonaws.com')) return 'https://api.pathmarkr.com';
    return (host === 'localhost' || host === '127.0.0.1') ? 'http://localhost:5000' : 'https://api.pathmarkr.com';
  })();

  const nextSlot = (accounts) => {
    if (!Array.isArray(accounts) || accounts.length === 0) return 1;
    const slots = accounts.map(a => Number(a.account_slot) || 0);
    for (let i = 1; i <= 3; i++) if (!slots.includes(i)) return i;
    return accounts.length + 1;
  };

  const connectGeotab = () => navigate('/client/addins');

  const connectMeta = () => {
    const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';
    const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/meta/callback`);
    const scope = encodeURIComponent('public_profile,ads_management,ads_read,pages_read_engagement');
    const state = encodeURIComponent(JSON.stringify({ userId: currentUser?.id, timestamp: Date.now(), slotIndex: nextSlot(metaAccs) }));
    window.location.href = `https://www.facebook.com/v20.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=code`;
  };

  const connectGoogle = () => {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';
    const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/google-ads/callback`);
    const scope = encodeURIComponent('openid email profile https://www.googleapis.com/auth/adwords');
    const state = encodeURIComponent(JSON.stringify({ userId: currentUser?.id, timestamp: Date.now(), slotIndex: nextSlot(googleAccs) }));
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&response_type=code&access_type=offline&prompt=consent`;
  };

  return (
    <div className="mcp-page" style={{ padding: 0 }}>
      <McpHeader title="Connections" />

      <div className="mx-auto w-full max-w-5xl p-6 md:p-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold text-ink">Your connections</h2>
            <p className="mt-1 text-sm text-ink-200">
              Geotab feeds in. Meta + Google push out. PathMarkr is the wire between them.
            </p>
          </div>
          <div className="flex gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">Connected</div>
              <div className="font-display text-xl font-bold text-ink">{loading ? '—' : connectedCount}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">Available</div>
              <div className="font-display text-xl font-bold text-ink">3</div>
            </div>
          </div>
        </div>

        {error && (
          <Alert message={error} type="error" showIcon closable
            style={{ marginBottom: 16, borderRadius: 12 }} onClose={() => setError(null)} />
        )}

        {loading ? (
        <div style={{ textAlign: 'center', padding: '64px 0' }}>
          <Spin size="large" tip="Checking connections…" />
        </div>
      ) : (
        <>
          {/* Cards grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20, marginBottom: 32 }}>

            {/* ── Geotab ── */}
            <ConnectionCard
              icon={<CarOutlined style={{ color: geotab ? '#fff' : 'var(--ink-300)' }} />}
              title="Geotab"
              category="Telematics · Fleet data in"
              description="Read-only access to LogRecord, Device, and Trip data. Synced to keep your fleet map current."
              connected={!!geotab}
              accountCount={geotabAccs.length}
              details={geotab ? [
                { label: 'Database',   value: geotab.database },
                { label: 'Username',   value: geotab.username },
                { label: 'Server',     value: (geotab.base_url || 'my.geotab.com').replace(/^https?:\/\//, '') },
                { label: 'Last saved', value: formatRelative(geotab.updated_at || geotab.created_at) },
              ] : null}
              onConnect={connectGeotab}
              onReauth={connectGeotab}
              onDisconnect={disconnectGeotab}
              disconnectLoading={disconnecting === 'geotab'}
            />

            {/* ── Meta / Facebook ── */}
            <ConnectionCard
              icon={<span style={{ color: meta ? '#fff' : 'var(--ink-300)', fontWeight: 900, fontSize: 20, lineHeight: 1 }}>f</span>}
              title="Facebook / Meta"
              category="Ads · Audiences out"
              description="Push geo-fenced stop locations as Saved Audience targets. Your ad account, your spend — PathMarkr builds the audience."
              connected={!!meta}
              accountCount={metaAccs.length}
              details={meta ? [
                { label: 'Account',   value: meta.account_name || meta.accountName },
                { label: 'Email',     value: meta.account_email || meta.accountEmail },
                { label: 'Slot',      value: meta.account_slot ? `Slot ${meta.account_slot}` : '—' },
                { label: 'Connected', value: formatRelative(meta.connected_at || meta.created_at) },
              ] : null}
              onConnect={connectMeta}
              onReauth={connectMeta}
              onDisconnect={disconnectMeta}
              disconnectLoading={disconnecting === 'meta'}
            />

            {/* ── Google Ads ── */}
            <ConnectionCard
              icon={<ApiOutlined style={{ color: google ? '#fff' : 'var(--ink-300)' }} />}
              title="Google Ads"
              category="Ads · Audiences out"
              description="Push stop pins as Google location targets with bid modifiers. Your MCC, your campaigns — PathMarkr drives the audience."
              connected={!!google}
              accountCount={googleAccs.length}
              details={google ? [
                { label: 'Account',    value: google.account_name || google.accountName },
                { label: 'Email',      value: google.account_email || google.accountEmail },
                { label: 'Customer ID',value: google.login_customer_id || google.google_user_id },
                { label: 'Connected',  value: formatRelative(google.connected_at || google.created_at) },
              ] : null}
              onConnect={connectGoogle}
              onReauth={connectGoogle}
              onDisconnect={disconnectGoogle}
              disconnectLoading={disconnecting === 'google'}
            />
          </div>

          {/* Concierge callout */}
          <div style={{
            borderRadius: 20, border: '2px solid var(--paper-300)',
            background: 'var(--paper-50)', padding: '24px 28px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)', marginBottom: 4 }}>
                Need to add another ad account?
              </div>
              <p style={{ fontSize: 13, color: 'var(--ink-200)', margin: 0, maxWidth: 480 }}>
                Each user supports up to 3 Meta slots and 3 Google Ads slots. Connect additional accounts from the Integrations page.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button type="button" className="mcp-btn-ghost" onClick={() => navigate('/client/integrations')}>
                Manage integrations →
              </button>
              <button type="button" className="mcp-btn-ghost" onClick={() => navigate('/client/support')}>
                Get concierge help
              </button>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default McpConnections;
